import { z } from 'zod';

/**
 * Wave 27 — Curva ABC (Pareto análise de compras).
 *
 * The classic Pareto cut on purchased items per project:
 *
 *   * Rank items by total committed spend (descending).
 *   * Classify:
 *       A = the top items that together represent ≤80% of spend,
 *       B = the next tranche up to 95%,
 *       C = the tail (everything else).
 *
 * The aggregation reads `purchase_orders.items_json` rows (scoped to
 * the project, excluding `cancelled` POs), groups by a normalized item
 * key (lowercased, trimmed name), and totals `subtotalCents`.
 *
 * We also return a best-effort supplier attribution per item — the
 * supplier whose POs contributed the most to that item's total spend.
 * Ties break on most-recent issued_at.
 */

export const abcClassSchema = z.enum(['A', 'B', 'C']);
export type AbcClass = z.infer<typeof abcClassSchema>;

export const curvaAbcRowSchema = z.object({
  /** Stable key used for sort-then-classify. Lowercase trimmed item name. */
  itemKey: z.string(),
  /** Original item name as it appeared on the first PO we saw. */
  displayName: z.string(),
  unit: z.string(),
  /** Total committed quantity summed across POs. */
  totalQty: z.number(),
  /** Total committed spend in cents (sum of subtotalCents across POs). */
  totalCents: z.number().int().nonnegative(),
  /** % of total spend this item represents. 0-100, two decimals. */
  percentOfTotal: z.number(),
  /** Running sum of percentOfTotal after sort — used for the curve. */
  cumulativePercent: z.number(),
  classification: abcClassSchema,
  /** Supplier with the largest committed spend on this item, if any. */
  topSupplierId: z.string().nullable(),
  topSupplierName: z.string().nullable(),
  /** Number of distinct POs this item appears in. */
  poCount: z.number().int().nonnegative(),
});
export type CurvaAbcRow = z.infer<typeof curvaAbcRowSchema>;

export const curvaAbcReportSchema = z.object({
  project: z.object({ id: z.string(), name: z.string() }),
  totals: z.object({
    totalCents: z.number().int().nonnegative(),
    itemCount: z.number().int().nonnegative(),
    /** How many distinct POs fed this aggregation (cancelled excluded). */
    poCount: z.number().int().nonnegative(),
    classCounts: z.object({
      A: z.number().int().nonnegative(),
      B: z.number().int().nonnegative(),
      C: z.number().int().nonnegative(),
    }),
  }),
  rows: z.array(curvaAbcRowSchema),
  generatedAt: z.string(),
});
export type CurvaAbcReport = z.infer<typeof curvaAbcReportSchema>;
